import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import type { GoogleOpeningHoursDisplay } from '@/utils/googlePlaceOverview';

const ROW = 'flex items-start gap-3';
/** Mobile "Practical information": 22px icons, gray values, 3px under the label. */
const ROW_ICON = { fontSize: 24, flexShrink: 0, color: '#222222', '@media (max-width: 767px)': { fontSize: 22 } } as const;
const ROW_CONTENT = 'flex min-w-0 flex-1 flex-col max-md:gap-[3px]';
const LABEL = 'm-0 font-manrope text-sm font-semibold leading-5 tracking-[0.02em] text-[#252528] max-md:text-black';
const VALUE = 'm-0 font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-[#252528] max-md:text-[#525257]';

/** "https://www.example.com/path/" → "example.com/path" for the Website row. */
function formatWebsiteLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname.replace(/\/$/, '');
    return `${parsed.hostname.replace(/^www\./, '')}${path}`;
  } catch {
    return url;
  }
}

interface PlaceModalAboutProps {
  description?: string;
}

/** Figma "AISummarySection": About heading + place summary, or a placeholder until one exists. */
export const PlaceModalAbout: React.FC<PlaceModalAboutProps> = ({ description }) => (
  <section className="flex flex-col gap-2 p-2.5 max-md:gap-3 max-md:p-0">
    <h3 className={clsx(LABEL, 'max-md:font-lora max-md:text-lg max-md:leading-6 max-md:tracking-[0.5px] max-md:text-[#252528]')}>
      About
    </h3>
    {description ? (
      <p className="m-0 font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-[#252528]">
        {description}
      </p>
    ) : (
      <p className="m-0 font-manrope text-sm font-normal italic leading-5 tracking-[0.02em] text-[#A7A7AE]">
        No description available yet.
      </p>
    )}
  </section>
);

interface PlaceModalInfoRowsProps {
  address?: string;
  directionsUrl: string | null;
  hours: GoogleOpeningHoursDisplay;
  website?: string;
  /** Restaurants (admin-set): "Menu" row from the design. */
  menuUrl?: string;
  /** Restaurants (admin-set): reservation link or OpenTable page. */
  tableBookingUrl?: string;
  phone?: string;
  /** Mobile: the Google rating card, beside the phone row (or on its own row without a phone). */
  phoneAside?: React.ReactNode;
}

const LINK_ROW_VALUE =
  'self-start font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-black underline hover:opacity-70';

/** Figma "PlaceInfoSection": address + directions, hours (expandable), menu, reservations, website, phone. */
export const PlaceModalInfoRows: React.FC<PlaceModalInfoRowsProps> = ({
  address,
  directionsUrl,
  hours,
  website,
  menuUrl,
  tableBookingUrl,
  phone,
  phoneAside,
}) => {
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const canExpandHours = hours.weekdayLines.length > 1;

  useEffect(() => {
    setHoursExpanded(false);
  }, [hours.summaryLine]);

  const hasAddress = !!address && address !== 'Address not available';
  if (!hasAddress && !hours.summaryLine && !website && !menuUrl && !tableBookingUrl && !phone && !phoneAside) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3.5 px-2.5 pb-1 pt-2.5 max-md:gap-5 max-md:p-0">
      {hasAddress && (
        <div className={ROW}>
          <PlaceOutlinedIcon sx={ROW_ICON} aria-hidden />
          <div className={clsx(ROW_CONTENT, 'gap-1')}>
            <p className={LABEL}>Address</p>
            <p className={VALUE}>{address}</p>
            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="self-start font-manrope text-xs font-semibold leading-4 tracking-[0.02em] text-[#FF5212] no-underline hover:underline"
              >
                Get directions
              </a>
            )}
          </div>
        </div>
      )}

      {hours.summaryLine && (
        <div className={ROW}>
          <AccessTimeIcon sx={ROW_ICON} aria-hidden />
          <div className={ROW_CONTENT}>
            <p className={LABEL}>Hours</p>
            <button
              type="button"
              disabled={!canExpandHours}
              aria-expanded={canExpandHours ? hoursExpanded : undefined}
              onClick={() => setHoursExpanded((v) => !v)}
              className={clsx(
                'flex w-full items-center gap-2 border-none bg-transparent p-0 text-left',
                canExpandHours ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <span className={clsx(VALUE, 'min-w-0 flex-1')}>{hours.summaryLine}</span>
              {canExpandHours && (
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    flexShrink: 0,
                    color: '#525257',
                    transform: hoursExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                  aria-hidden
                />
              )}
            </button>
            {hoursExpanded && canExpandHours && (
              <ul className="m-0 mt-2 list-none space-y-1 border-l-2 border-[#ececf0] pl-3 font-manrope text-xs leading-[1.35] text-[#525257]">
                {hours.weekdayLines.map((line, i) => (
                  <li key={`${i}-${line.slice(0, 24)}`}>{line}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {menuUrl && (
        <div className={ROW}>
          <MenuBookOutlinedIcon sx={ROW_ICON} aria-hidden />
          <div className={ROW_CONTENT}>
            <p className={LABEL}>Menu</p>
            <a href={menuUrl} target="_blank" rel="noopener noreferrer" className={LINK_ROW_VALUE}>
              View menu
            </a>
          </div>
        </div>
      )}

      {tableBookingUrl && (
        <div className={ROW}>
          <EventAvailableOutlinedIcon sx={ROW_ICON} aria-hidden />
          <div className={ROW_CONTENT}>
            <p className={LABEL}>Reservations</p>
            <a href={tableBookingUrl} target="_blank" rel="noopener noreferrer" className={LINK_ROW_VALUE}>
              Reserve a table
            </a>
          </div>
        </div>
      )}

      {website && (
        <div className={ROW}>
          <OpenInNewIcon sx={ROW_ICON} aria-hidden />
          <div className={ROW_CONTENT}>
            <p className={LABEL}>Website</p>
            <a
              href={website}
              target="_blank"
              rel="noopener noreferrer"
              className="break-all font-manrope text-sm font-normal leading-5 tracking-[0.02em] text-[#1A73E8] underline max-md:no-underline"
            >
              {formatWebsiteLabel(website)}
            </a>
          </div>
        </div>
      )}

      {(phone || phoneAside) && (
        <div className="flex items-start gap-3">
          {phone && (
            <div className={clsx(ROW, 'min-w-0 flex-1')}>
              <PhoneOutlinedIcon sx={ROW_ICON} aria-hidden />
              <div className={ROW_CONTENT}>
                <p className={LABEL}>Phone</p>
                <a href={`tel:${phone.replace(/\s+/g, '')}`} className={clsx(VALUE, 'no-underline hover:underline')}>
                  {phone}
                </a>
              </div>
            </div>
          )}
          {phoneAside}
        </div>
      )}
    </section>
  );
};
